import { convertPersianNumbersToEnglishNumbers } from '../../helpers/numbers'
import {
  PaymentFollowUpFilter,
  PaymentFollowUpReason,
  PaymentFollowUpState
} from '../../types/paymentFollowUps'
import { FOLLOW_UP_REASONS } from './labels'

export interface FollowUpFilters {
  state: PaymentFollowUpState
  reason: PaymentFollowUpReason | ''
  reservationNumber: number | null
}

/** A positive, exactly representable reservation number (Persian digits allowed). */
export function parseReservationNumber(input: string): number | null {
  const digits = convertPersianNumbersToEnglishNumbers(input.trim())
  if (!/^\d+$/.test(digits)) return null
  const value = Number(digits)
  return Number.isSafeInteger(value) && value > 0 ? value : null
}

export function followUpFilter(
  state: PaymentFollowUpState,
  reason: PaymentFollowUpReason | '',
  reservationNumber: number | null
): PaymentFollowUpFilter {
  return {
    state,
    ...(reason !== '' ? { reason } : {}),
    ...(reservationNumber !== null
      ? { displayReservationNumber: reservationNumber }
      : {})
  }
}

const defaults = (): FollowUpFilters => ({
  state: 'Open',
  reason: '',
  reservationNumber: null
})

/** The address bar is untrusted: malformed/unknown values fall back to Open. */
export function readFollowUpFilters(search: string): FollowUpFilters {
  const raw = new URLSearchParams(search).get('filter')
  if (!raw) return defaults()
  try {
    const value: unknown = JSON.parse(raw)
    if (!value || typeof value !== 'object' || Array.isArray(value))
      return defaults()
    const filter = value as Record<string, unknown>
    if (filter.state !== 'Open' && filter.state !== 'Resolved')
      return defaults()
    const reason =
      FOLLOW_UP_REASONS.find((item) => item === filter.reason) ?? ''
    if (filter.reason !== undefined && filter.reason !== '' && reason === '')
      return defaults()
    const reservationNumber =
      typeof filter.reservationNumber === 'number'
        ? parseReservationNumber(String(filter.reservationNumber))
        : null
    if (filter.reservationNumber !== undefined && reservationNumber === null)
      return defaults()
    return { state: filter.state, reason, reservationNumber }
  } catch {
    return defaults()
  }
}

export function writeFollowUpFilters(
  search: string,
  filters: FollowUpFilters
): string {
  const params = new URLSearchParams(search)
  if (
    filters.state === 'Open' &&
    filters.reason === '' &&
    filters.reservationNumber === null
  ) {
    params.delete('filter')
  } else {
    params.set(
      'filter',
      JSON.stringify({
        state: filters.state,
        ...(filters.reason ? { reason: filters.reason } : {}),
        ...(filters.reservationNumber !== null
          ? { reservationNumber: filters.reservationNumber }
          : {})
      })
    )
  }
  const query = params.toString()
  return query ? `?${query}` : ''
}
